import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  ledgerProviderCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { ledgerEndpoints } from "@/features/ledger/api/endpoints";
import type { ProviderDto } from "@/features/ledger/api/dto";

/**
 * Met à jour un provider (`PUT /Providers/{id}`) — réservé à
 * flashpay-superadmin / flashpay-admin.
 */
export const useUpdateProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["ledger", "providers", "update"],
    mutationFn: ({
      providerId,
      body,
    }: {
      providerId: string;
      body: ProviderDto;
    }) =>
      mutateData({
        url: ledgerEndpoints.providers.update(providerId),
        reqBody: body as unknown as Record<string, unknown>,
        method: "PUT",
      }) as Promise<ProviderDto>,
    onSuccess: (_data, { providerId }) => {
      return invalidateAfterMutation(
        queryClient,
        ledgerProviderCacheKeys(providerId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useUpdateProvider failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "ledger" }
      );
    },
  });
};
